import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { Button, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import FormInputErrorMessage from 'common/form-input-error-message';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';

import { AssignedTaskMatterInfoType } from './assigned-task-matter-info';

const useStyles = makeStyles(() => ({
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  columnHeader: {
    width: '200px',
  },
  column: {
    display: 'flex',
    width: '100%',
    padding: '20px 0',
    borderBottom: '1px solid #ECEEF1',
    alignItems: 'center',
  },
  columnBody: {
    display: 'flex',
    flex: 1,
  },
  fieldRequired: {
    color: 'red',
  },
  addButton: {
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    marginTop: '20px',
  },
  title: {
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    marginBottom: '10px',
    '& button': {
      backgroundColor: '#F5F5F5',
      borderRadius: '8px',
      marginLeft: '30px',
    },
  },
  subTitle: {
    fontSize: '18px',
    marginTop: '20px',
  },
}));

const AssignedTaskMatterInfoTrustadminGrantorsForm = () => {
  const classes = useStyles();

  const {
    control,
    formState: { errors },
  } = useFormContext<AssignedTaskMatterInfoType>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'estateTrustadminFields.estateGrantors',
  });

  const primaryGrantorIndex = fields.findIndex((item) => item.isPrimaryGrantor);

  const errorMessage =
    errors.estateTrustadminFields?.estateGrantors?.message ||
    errors.estateTrustadminFields?.estateGrantors?.root?.message;

  const renderGrantorFields = (item: any, index: number) => {
    return (
      <div key={`${index}${item.id}`}>
        {index !== primaryGrantorIndex && (
          <div className={classes.title}>
            <div>Grantor 2</div>
            <Button variant="contained" type="button" onClick={() => remove(index)}>
              Remove
            </Button>
          </div>
        )}
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <span className={classes.fieldRequired}>*</span>Grantor First Name
            </Typography>
          </div>
          <div className={classes.columnBody}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorFirstName`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorFirstName
                      ?.message
                  }
                />
              )}
            />
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <span className={classes.fieldRequired}>*</span>Grantor Last Name
            </Typography>
          </div>
          <div className={classes.columnBody}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorLastName`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormTextField
                  {...field}
                  inputRef={ref}
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorLastName?.message
                  }
                />
              )}
            />
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>Grantor SSN</Typography>
          </div>
          <div className={classes.columnBody}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.grantorSsn`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormSsnField
                  {...field}
                  inputRef={ref}
                  errorMessage={
                    errors.estateTrustadminFields?.estateGrantors?.[index]?.grantorSsn?.message
                  }
                />
              )}
            />
          </div>
        </div>
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>Is Grantor Still Alive?</Typography>
          </div>
          <div className={classes.columnBody}>
            <Controller
              name={`estateTrustadminFields.estateGrantors.${index}.isGrantorStillAlive`}
              control={control}
              render={({ field: { ref, value, onChange, ...field } }) => (
                <FormSelectField
                  {...field}
                  value={value === true ? 'yes' : 'no'}
                  onChange={(e) => onChange(e.target.value === 'yes')}
                  inputRef={ref}
                  options={[
                    {
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      label: 'No',
                      value: 'no',
                    },
                  ]}
                />
              )}
            />
          </div>
        </div>
        {index === primaryGrantorIndex && (
          <>
            <Typography className={classes.subTitle}>
              Grantor’s Last Address Street, City, State, Zip
            </Typography>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Grantor Address Street</Typography>
              </div>
              <div className={classes.columnBody}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressStreet`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} />
                  )}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Grantor Address City</Typography>
              </div>
              <div className={classes.columnBody}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressCity`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} />
                  )}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Grantor Address State</Typography>
              </div>
              <div className={classes.columnBody}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressState`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} />
                  )}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography className={classes.headerText}>Grantor Address Zip</Typography>
              </div>
              <div className={classes.columnBody}>
                <Controller
                  name={`estateTrustadminFields.estateGrantors.${index}.grantorAddressZip`}
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <FormTextField {...field} inputRef={ref} />
                  )}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderNonPrimaryGrantorFields = (item: any, index: number) => {
    if (item.isPrimaryGrantor) {
      return null;
    }
    return renderGrantorFields(item, index);
  };

  return (
    <>
      {primaryGrantorIndex > -1 &&
        renderGrantorFields(fields[primaryGrantorIndex], primaryGrantorIndex)}
      <div>{errorMessage && <FormInputErrorMessage error={errorMessage} />}</div>
      {fields.map((item, index) => renderNonPrimaryGrantorFields(item, index))}
      {fields.length < 2 && (
        <Button
          variant="contained"
          type="button"
          className={classes.addButton}
          onClick={() =>
            append({
              grantorFirstName: '',
              grantorLastName: '',
              grantorSsn: '',
              isGrantorStillAlive: false,
              isPrimaryGrantor: false,
            })
          }
        >
          <AddIcon /> Add Grantor
        </Button>
      )}
    </>
  );
};

export default AssignedTaskMatterInfoTrustadminGrantorsForm;
